import React, { useState } from 'react';

import { openBlob } from '@apis/common';
import { pdfApis } from '@apis/next/pdfApis';
import { preAlertApis } from '@apis/next/preAlertApis';
import { TrackingDataDetails } from '@components/admin/booking/TrackingDataDetails';
import { Button } from '@components/Button';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { FormTitle } from '@components/Forms/FormTitle';
import { Layout } from '@components/Layout';
import { BookingInquiryDisplay } from '@components/utilities/BookingInquiry/BookingInquiryDisplay';
import { BookingInquiryInformation } from '@components/utilities/Bookings/BookingInquiryInformation';
import { BookingInquiryShareDialog } from '@components/utilities/Bookings/BookingInquiryShareDialog';
import { BookingsContainer } from '@components/utilities/Bookings/BookingsContainer';
import { CancelBookingDialog } from '@components/utilities/Bookings/CancelBookingDialog';
import { InstructionFormEntrances } from '@components/utilities/Bookings/InstructionFormEntrances';
import { ShareDialog } from '@components/utilities/Bookings/ShareDialog';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { bookingsStrings } from '@data/bookings';
import { useSearch } from '@hooks/next/useSearch';
import { useBookingInquiryDetail } from '@hooks/queries/useBookingInquiryDetail';
import { useUser } from '@hooks/queries/useUser';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMutation } from '@hooks/useMutation';
import Collapse from '@material-ui/core/Collapse';
import { clientInquiryStatusMap } from '@models/BookingInquiry';
import { CargoTrackingResultQuery } from '@pages/utilities/CargoTracking/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime, stringifySearch, toJST } from '@utils/types';
import clsx from 'clsx';
import { z } from 'zod';

const useStyles = makeMuiStyles((_theme, _common) => ({}));

const strings = createStrings({
  ja: {
    bookingInformation: '予約情報',
    cancelBooking: '予約をキャンセル',
    booking: '予約',
    status: 'ステータス',
    datetime: '日時',
    deliveryStatus: '配達状況',
    remark: '備考',
    document: '書類',
    share: '共有',
    print: '印刷',
    bookingContent: '予約内容確認',
    changeLog: '変更履歴',
  },
});

export interface BookingDetailsProps {
  type: 'current' | 'past';
}

export const BookingDetails: React.FC<BookingDetailsProps> = ({ type }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const [detailsOpen, setDetailsOpen] = useState(true);

  const {
    search: { d2dNo },
  } = useSearch(
    z.object({
      d2dNo: z.string(),
    }),
  );

  const [UIState, setUIState] = useState(() => ({
    cancelBookingDialog: {
      open: false,
    },
    shareDialog: {
      open: false,
    },
  }));
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const bookingInquiryDetail = useBookingInquiryDetail(d2dNo);
  const preAlert = preAlertApis.useGet('/pre-alert/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });
  const user = useUser();

  const isCreator = !!bookingInquiryDetail.data?.can_views.find(
    (canView) => canView.user === user.data?.id && canView.can_view_type === 'creator',
  );

  const loading = preAlert.isLoading || bookingInquiryDetail.isLoading;

  const searchString = stringifySearch<CargoTrackingResultQuery>({ trackNos: [d2dNo] });
  const shareUrl = `${url('/utilities/CargoTracking/CargoTrackingResult')}?${searchString}`;

  const downloadBookingInquiryDisplayPdf = useMutation(() =>
    pdfApis.client.get('/pdf/BookingInquiryDisplay/:d2d_no', { params: { d2d_no: d2dNo } }),
  );

  const downloadBookingInquiryPriceSettlement = useMutation(() =>
    pdfApis.client.get('/pdf/BookingInquiryPriceSettlement/:d2d_no', { params: { d2d_no: d2dNo } }),
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [downloadBookingInquiryDisplayPdf, downloadBookingInquiryPriceSettlement],
  });

  return (
    <Layout
      loading={loading}
      title={`${bookingsStrings.currentBookings} - ${d2dNo}`}
      errors={[preAlert.error, bookingInquiryDetail.error]}
      breadcrumbs={[
        '/utilities/Dashboard',
        type === 'current'
          ? '/utilities/Bookings/CurrentBookings'
          : '/utilities/Bookings/PastBookings',
        {
          text: d2dNo as string,
        },
      ]}
    >
      <CancelBookingDialog
        open={UIState.cancelBookingDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, cancelBookingDialog: { open: false } };
          });
        }}
      />
      <ShareDialog
        open={UIState.shareDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, shareDialog: { open: false } };
          });
        }}
        url={`${window.location.protocol}//${window.location.host}${shareUrl}`}
      />
      <BookingInquiryShareDialog
        inquiry={bookingInquiryDetail.data!}
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
        }}
      />
      <BookingsContainer>
        <div>
          <FormTitle
            title={strings.bookingInformation}
            actions={
              <Button
                color="grey"
                text={strings.cancelBooking}
                size="small"
                onClick={() => {
                  setUIState((state) => {
                    return { ...state, cancelBookingDialog: { open: true } };
                  });
                }}
                type="button"
                disabled={
                  ['客側キャンセル済', 'FCキャンセル済'].includes(
                    bookingInquiryDetail.data?.status ?? '',
                  ) || !isCreator
                }
              />
            }
          />
          <BookingInquiryInformation
            d2dNo={d2dNo}
            createdAt={
              bookingInquiryDetail.data?.created_at == null
                ? ''
                : formatDateTime(bookingInquiryDetail.data?.created_at)
            }
            status={clientInquiryStatusMap(bookingInquiryDetail.data?.status ?? '-')}
            onShare={() => {
              setShareDialogOpen(true);
            }}
            unsharable={!isCreator}
          />
        </div>
        <InstructionFormEntrances d2dNo={d2dNo} />
        <FormTitle
          title={strings.status}
          actions={
            <div className={clsx(classes.flexInline, classes.horizontalSpace1)}>
              <Button
                color="primary"
                text={strings.share}
                size="small"
                onClick={() => {
                  setUIState((state) => {
                    return { ...state, shareDialog: { open: true } };
                  });
                }}
                type="button"
              />
              <a
                href={`${url('/utilities/Bookings/PrintCurrentBookingDetails')}?d2dNo=${d2dNo}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button color="primary" text={strings.print} size="small" type="button" />
              </a>
            </div>
          }
        />
        {d2dNo && preAlert.data && (
          <>
            <TrackingDataDetails
              bookingId={preAlert.data.booking_inquiry}
              d2dNo={d2dNo}
              preAlert={preAlert.data}
              isClient
            />

            <div style={{ height: '1rem' }} />
            <a className={classes.baseButton} onClick={() => setDetailsOpen(!detailsOpen)}>
              <FormTitle
                title={strings.bookingContent}
                actions={
                  <div className={clsx(classes.flexInline, classes.horizontalSpace1)}>
                    <Button
                      color="primary"
                      text={bookingInquiryStrings.printBookingInquiryDisplay}
                      size="small"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const blob = await downloadBookingInquiryDisplayPdf.mutateAsync();
                        openBlob(
                          blob,
                          `${bookingInquiryStrings.printBookingInquiryDisplay}.pdf`,
                          'application/pdf',
                        );
                      }}
                      type="button"
                    />

                    <Button
                      color="primary"
                      text={bookingInquiryStrings.printBookingInquiryPriceSettlement}
                      size="small"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const blob = await downloadBookingInquiryPriceSettlement.mutateAsync();
                        openBlob(
                          blob,
                          `${bookingInquiryStrings.printBookingInquiryPriceSettlement}.pdf`,
                          'application/pdf',
                        );
                      }}
                      type="button"
                    />

                    <ExpandMoreButton
                      expanded={detailsOpen}
                      color="primary"
                      style={{ position: 'relative', top: 3 }}
                    />
                  </div>
                }
              />
            </a>

            {renderAlertsAndSpinnerModal()}

            <Collapse in={detailsOpen}>
              <BookingInquiryDisplay
                variant="content"
                bookingInquiry={bookingInquiryDetail.data!}
              />
              <div style={{ height: '1rem' }} />
              <div
                className={clsx(
                  classes.textGray800,
                  classes.middleText,
                  classes.flexInline,
                  classes.justifyBetween,
                )}
              >
                <span>
                  Latest Update: {toJST(new Date(bookingInquiryDetail.data?.updated_at ?? ''))}
                </span>

                <a
                  target="_blank"
                  rel="noreferrer"
                  href={`${url('/utilities/Bookings/BookingInquiryLogs')}?d2d_no=${
                    bookingInquiryDetail.data?.d2d_no
                  }`}
                  className={clsx(classes.link, classes.baseButton)}
                >
                  {strings.changeLog}
                </a>
              </div>
            </Collapse>
          </>
        )}
      </BookingsContainer>
    </Layout>
  );
};
